import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Grid } from '@mui/material';
import PropTypes from 'prop-types';

import { BarStack, Bar, LinePath } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import _ from 'lodash';

import {
  getParams,
  getMonth,
  thousandFormat,
  thousandFormat2,
  getMonths,
  financialMonth,
} from '../../../../Utils/Functions';
import {
  enMonths,
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  activeLabelColor,
} from '../../../../Assets/js/constant';

// accessors
const xAccessor = (d) => d.month;

let tooltipTimeout;

const TopChart = ({
  summaryData,
  selectedYears,
  selectedMonths,
  selectedTopItems,
  defaultStartMonth,
  handleFilter,
}) => {
  const canvasRef = useRef(null);
  const {
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [ytdTotalActualArr, setYtdTotalActualArr] = useState({});
  const [ytdTotalForecastArr, setYtdTotalForecastArr] = useState({});
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [colors, setColors] = useState([]);
  const [xTransform, setXTransform] = useState(0);
  const [X, setX] = useState(0);
  const [iX, setIX] = useState(0);
  const [iY, setIY] = useState(0);
  const [rX, setRX] = useState(0);
  const [rY, setRY] = useState(0);
  const [rW, setRW] = useState(0);
  const [rH, setRH] = useState(0);
  const [canvasIndex, setCanvasIndex] = useState(-1);
  const [dragFlag, setDragFlag] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  const prepareData = useCallback(() => {
    let dictData = {},
        dictTotals = {},
        dictForecasts = {},
        dataArray = [],
        totalsArray = [],
        forecastsArray = [];

    let colorsArray = selectedYears.reduce((ret, year) => {
      ret.push(positiveActiveColor);
      return ret;
    }, []);

    let fiscalMonths = getMonths(defaultStartMonth);
    fiscalMonths.forEach((month) => {
      dictData[month] = {};
      dictData[month]['month'] = month;
      dictData[month]['value'] = {};

      selectedYears.forEach((year) => {
        dictData[month][year] = 0;
      });

      dictTotals[month] = 0;
      dictForecasts[month] = 0;
    });

    let ytdIncomeTotalCalc = 0;
    let ytdForecastTotalCalc = 0;
    let ytdTotalActualArrCalc = {};
    let ytdTotalForecastArrCalc = {};
    
    // group by FY + Date to aggregate per fiscal year and month
    let _summaryData = _.map(
      _.groupBy(summaryData, (row) => `${row.FY}|${row.Date}`),
      function (vals, key) {
        const [, Date] = key.split('|');
        return _.reduce(
          vals,
          function (m, o) {
            for (var p in o)
              if (p !== 'Date') {
                if (
                  p === 'ActualFees' ||
                  p === 'BudgetFees' ||
                  p === 'ForecastFees'
                )
                  m[p] = (parseFloat(m[p]) || 0) + parseFloat(o[p]);
                else m[p] = o[p];
              }

            return m;
          },
          { Date: Date },
        );
      },
    );

    _summaryData.forEach((item) => {
      let month = getMonth(item.Date);
      if (dictData[month] !== undefined) {
        dictData[month][item.FY] = parseFloat(item.ActualFees);
        dictData[month]['value'][item.FY] = item;
        dictTotals[month] += parseFloat(item.ActualFees);
        dictForecasts[month] += parseFloat(item.ForecastFees);
      }
    });

    fiscalMonths.forEach((month) => {
      ytdIncomeTotalCalc += dictTotals[month];
      ytdForecastTotalCalc += dictForecasts[month];
      ytdTotalActualArrCalc[month] = ytdIncomeTotalCalc;
      ytdTotalForecastArrCalc[month] = ytdForecastTotalCalc;
    });

    fiscalMonths.forEach((month) => {
      dataArray.push(dictData[month]);
      totalsArray.push(dictTotals[month]);
      forecastsArray.push(dictForecasts[month]);
    });

    let xTransformValue = 0;
    let maxYValue = Math.max(
      Math.abs(Math.min(Math.min(...totalsArray), Math.min(...forecastsArray))),
      Math.max(Math.max(...totalsArray), Math.max(...forecastsArray)),
    );

    if (
      !isFinite(maxYValue) ||
      maxYValue === Number.NEGATIVE_INFINITY ||
      isNaN(maxYValue) ||
      maxYValue === 0
    )
      xTransformValue = 30;
    else {
      xTransformValue = 9.5 * thousandFormat2(maxYValue).length;
    }
    
    setData(dataArray);
    setTotals(totalsArray);
    setForecasts(forecastsArray);
    setColors(colorsArray);
    setYtdTotalActualArr(ytdTotalActualArrCalc);
    setYtdTotalForecastArr(ytdTotalForecastArrCalc);
    setXTransform(xTransformValue);
  }, [summaryData, selectedYears, defaultStartMonth]);

  const getColor = useCallback((bar) => {
    const year = bar.key;
    const month = bar.bar.data.month;

    const actual = bar.bar.data.value[year]
      ? parseInt(bar.bar.data.value[year].ActualFees)
      : 0;
    const forecast = bar.bar.data.value[year]
      ? parseInt(bar.bar.data.value[year].ForecastFees)
      : 0;

    const activeColor =
      actual < forecast ? negativeActiveColor : positiveActiveColor;
    const disableColor =
      actual < forecast ? negativeDisableColor : positiveDisableColor;

    if (selectedTopItems.length === 0 && selectedMonths.length === 0) {
      return activeColor;
    }

    if (selectedTopItems.length === 0)
      if (selectedMonths.indexOf(month) > -1) return activeColor;

    for (let i = 0; i < selectedTopItems.length; i++) {
      if (
        selectedTopItems[i].month === month &&
        selectedTopItems[i].year === year
      ) {
        return activeColor;
      }
    }

    return disableColor;
  }, [selectedMonths, selectedTopItems]);

  const getMousePositionOnCanvas = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const drawRectangle = (end = false) => {
    const context = canvasRef.current.getContext('2d');
    context.beginPath();
    context.lineWidth = '1';
    context.strokeStyle = '#00f3ff';
    context.rect(rX, rY, rW, rH);
    context.stroke();
    context.globalAlpha = 0.2;
    context.fillStyle = 'lightblue';
    context.fill();
    if (end) {
      drawSelectBar();
    }
  };

  const resetCanvas = () => {
    setX(0);
    setIX(0);
    setIY(0);
    setRX(0);
    setRY(0);
    setRW(0);
    setRH(0);
    setCanvasIndex(-1);
    setDragFlag(false);
    setIsMouseDown(false);

    const context = canvasRef.current.getContext('2d');
    context.clearRect(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height,
    );
    context.beginPath();
  };

  const drawSelectBar = () => {
    let unitWidth = (window.innerWidth - 16 - xTransform) / 12;
    let startPosition = X >= iX ? iX - xTransform : X - xTransform;
    let endPosition = X >= iX ? X - xTransform : iX - xTransform;
    let startMonth = Math.floor(startPosition / unitWidth);
    let endMonth = Math.floor(endPosition / unitWidth);
    let _selectedTopItems = [],
        _selectedMonths = [];

    for (let i = startMonth; i <= endMonth; i++) {
      let _month = financialMonth(i, defaultStartMonth);
      _month = _month === 12 ? 0 : _month;
      if (i + 1 < defaultStartMonth)
        _selectedTopItems.push({ month: _month, year: selectedYears[0] - 1 });
      else _selectedTopItems.push({ month: _month, year: selectedYears[0] });
      _selectedMonths.push(_month);
    }

    if (rX === 0 && rY === 0 && _selectedTopItems.length > 0) {
      if (
        _.isEqual(
          selectedTopItems[0],
          _selectedTopItems[_selectedTopItems.length - 1],
        )
      ) {
        if (selectedTopItems.length === _selectedTopItems.length)
          deSelectAll();
        else
          handleFilter({
            selectedTopItems: [_selectedTopItems[_selectedTopItems.length - 1]],
            selectedMonths: [_selectedMonths[_selectedTopItems.length - 1]],
          });
      } else
        handleFilter({
          selectedTopItems: [_selectedTopItems[_selectedTopItems.length - 1]],
          selectedMonths: [_selectedMonths[_selectedTopItems.length - 1]],
        });
    } else {
      if (
        _.isEqual(
          selectedTopItems[0],
          _selectedTopItems[_selectedTopItems.length - 1],
        )
      ) {
        if (selectedTopItems.length === _selectedTopItems.length)
          deSelectAll();
        else
          handleFilter({
            selectedTopItems: _selectedTopItems,
            selectedMonths: _selectedMonths,
          });
      } else
        handleFilter({
          selectedTopItems: _selectedTopItems,
          selectedMonths: _selectedMonths,
        });
    }

    resetCanvas();
  };

  const onMouseMove = (event) => {
    if (!dragFlag || !canvasRef.current) return;

    const pos = getMousePositionOnCanvas(event);
    setX(pos.x);
    setRX(Math.min(iX, pos.x));
    setRY(Math.min(iY, pos.y));
    setRW(iX ? Math.abs(iX - pos.x) : 0);
    setRH(iY ? Math.abs(iY - pos.y) : 0);

    const context = canvasRef.current.getContext('2d');
    context.clearRect(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height,
    );
    // drawRectangle will be called in useEffect when coordinates change
  };

  const onMouseOut = (event) => {
    if (iX && iY) {
      // drawRectangle(true);
    }
  };

  const endDraw = () => {
    if (iX && iY) {
      drawRectangle(true);
    }
  };

  const onSvgMouseDown = (event) => {
    if (event.button !== 0 || !canvasRef.current) return;

    const target = event.target;
    if (
      target &&
      (target.tagName === 'text' ||
        target.getAttribute('data-no-drag') === 'true')
    ) {
      return;
    }

    setIsMouseDown(true);

    if (dragFlag) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const startX = event.clientX - rect.left;
    const startY = event.clientY - rect.top;

    setCanvasIndex(0);
    setX(startX);
    setIX(startX);
    setIY(startY);
    setRX(startX);
    setRY(startY);
    setRW(0);
    setRH(0);
    setDragFlag(true);
  };

  const onSvgMouseMove = (event) => {
    if (!isMouseDown || dragFlag || event.buttons !== 1 || !canvasRef.current)
      return;

    const rect = canvasRef.current.getBoundingClientRect();
    const startX = event.clientX - rect.left;
    const startY = event.clientY - rect.top;

    setCanvasIndex(0);
    setX(startX);
    setIX(startX);
    setIY(startY);
    setRX(startX);
    setRY(startY);
    setRW(0);
    setRH(0);
    setDragFlag(true);
  };

  const onSvgMouseUp = () => {
    setIsMouseDown(false);
  };

  const onSvgMouseLeave = () => {
    setIsMouseDown(false);
  };

  const handleBar = useCallback((event, bar) => {
    if (dragFlag) return;

    const month = bar.bar.data.month;
    const year = bar.key;

    let index = NaN;
    for (let i = 0; i < selectedMonths.length; i++) {
      if (selectedMonths[i] === month) {
        index = i;
        break;
      }
    }

    if (event.shiftKey) {
      let _selectedBars = selectedBars.slice();
      let _selectedMonths = selectedMonths.slice();
      let _selectedTopItems = selectedTopItems.slice();

      if (isNaN(index)) {
        _selectedMonths.push(month);
        if (month + 1 < defaultStartMonth)
          _selectedTopItems.push({ month: month, year: selectedYears[0] });
        else
          _selectedTopItems.push({ month: month, year: selectedYears[0] - 1 });
      } else {
        _selectedMonths.splice(index, 1);
        _selectedTopItems.splice(index, 1);
      }

      setSelectedBars(_selectedBars);
      handleFilter({
        selectedTopItems: _selectedTopItems,
        selectedMonths: _selectedMonths,
      });
    } else {
      let _selectedMonths = [];
      let exist = false;
      
      if (month + 1 < defaultStartMonth) {
        exist = selectedTopItems.find(item => item.month === month && item.year === year);
      } else {
        exist = selectedTopItems.find(item => item.month === month && item.year === year - 1);
      }

      if (exist) {
        setSelectedBars([]);
        handleFilter({
          selectedTopItems: [],
          selectedMonths: [],
        });
      } else {
        let _selectedTopItems;
        if (month + 1 < defaultStartMonth)
          _selectedTopItems = [{ month: month, year: year }];
        else _selectedTopItems = [{ month: month, year: year - 1 }];
        
        _selectedMonths.push(month);
        event.target.classList.add('barActive');
        
        setSelectedBars([event.target]);
        handleFilter({
          selectedTopItems: _selectedTopItems,
          selectedMonths: _selectedMonths,
        });
      }
    }
  }, [selectedMonths, selectedTopItems, selectedBars, selectedYears, defaultStartMonth, handleFilter, dragFlag]);

  const handleLabel = useCallback((event, month) => {
    let index = NaN;
    for (let i = 0; i < selectedMonths.length; i++) {
      if (selectedMonths[i] === month) {
        index = i;
        break;
      }
    }

    if (event.shiftKey) {
      let _selectedBars = selectedBars.slice();
      let _selectedMonths = selectedMonths.slice();
      let _selectedTopItems = selectedTopItems.slice();

      if (isNaN(index)) {
        _selectedMonths.push(month);
        if (month + 1 < defaultStartMonth)
          _selectedTopItems.push({ month: month, year: selectedYears[0] });
        else
          _selectedTopItems.push({ month: month, year: selectedYears[0] - 1 });
      } else {
        _selectedMonths.splice(index, 1);
        _selectedTopItems.splice(index, 1);
      }

      setSelectedBars(_selectedBars);
      handleFilter({
        selectedMonths: _selectedMonths,
        selectedTopItems: _selectedTopItems,
      });
    } else {
      let _selectedBars = [];
      let _selectedTopItems = [];

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
      });

      let _selectedMonths;
      if (!isNaN(index) && selectedMonths.length === 1) {
        _selectedMonths = [];
      } else {
        _selectedMonths = [month];
        if (month + 1 < defaultStartMonth)
          _selectedTopItems = [{ month: month, year: selectedYears[0] }];
        else _selectedTopItems = [{ month: month, year: selectedYears[0] - 1 }];
      }

      setSelectedBars(_selectedBars);
      handleFilter({
        selectedMonths: _selectedMonths,
        selectedTopItems: _selectedTopItems,
      });
    }
  }, [selectedMonths, selectedTopItems, selectedBars, selectedYears, defaultStartMonth, handleFilter]);

  const deSelectAll = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    setHoverBar(null);
    setSelectedBars([]);
    localStorage.removeItem('feeTopItems');
    handleFilter({
      selectedMonths: [],
      selectedTopItems: [],
    });
  }, [hoverBar, selectedBars, handleFilter]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let tooltipDataObj = {},
        top,
        left;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);

      top = event.clientY - 75;
      left =
        window.innerWidth / 2 < event.nativeEvent.layerX
          ? event.nativeEvent.layerX - 230
          : event.nativeEvent.layerX + 50;
      tooltipDataObj = bar;
    } else {
      top = event.clientY - 70;
      left =
        window.innerWidth / 2 < event.nativeEvent.layerX
          ? event.nativeEvent.layerX - 235
          : event.nativeEvent.layerX + 50;
      tooltipDataObj['forecast'] = bar;
    }

    tooltipDataObj['isBar'] = isBar;

    let selSum = 0;
    let mtdMonths = 0;

    if (data && selectedTopItems.length > 1)
      data.forEach((item) => {
        selectedTopItems.forEach((subitem) => {
          if (item.month === subitem.month) {
            selSum += item.value[Object.keys(item.value)[0]].ActualFees;
            mtdMonths++;
          }
        });
      });

    tooltipDataObj['selSum'] = selSum;
    tooltipDataObj['items'] = mtdMonths;

    showTooltip({
      tooltipData: tooltipDataObj,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, data, selectedTopItems, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);

      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  // Component mount effect
  useEffect(() => {
    prepareData();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [prepareData, onResize]);

  // Component update effect for summaryData changes
  useEffect(() => {
    prepareData();
  }, [summaryData, prepareData]);

  // Effect to draw rectangle when coordinates change
  useEffect(() => {
    if (dragFlag && canvasRef.current && (rW > 0 || rH > 0)) {
      drawRectangle();
    }
  }, [rX, rY, rW, rH, dragFlag, drawRectangle]);

  const width = window.innerWidth - 16;
  const height = 150;
  const margin = {
    top: 10,
    right: 0,
    bottom: 20,
    left: xTransform,
  };

  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );
  const offsetX = 3.5;
  const offsetWidth = -1.2;
  let minY = Math.min(Math.min(...totals), Math.min(...forecasts));
  minY = Math.min(0, minY);
  minY = Math.floor(minY / 100000) * 100000;
  let maxY = Math.max(Math.max(...totals), Math.max(...forecasts));
  maxY = Math.max(0, maxY);

  // scales
  const xScale = scaleBand()
    .domain(data.map(xAccessor))
    .padding(0.2)
    .rangeRound([0, xMax]);

  const yScale = scaleLinear({
    domain: [minY, maxY * 1.1],
    range: [yMax, 0],
    nice: true,
  });
  
  const color = scaleOrdinal({
    domain: selectedYears,
    range: colors,
  });

  let fiscalMonths = getMonths(defaultStartMonth);
  
  return (
    <div id="fee-top-chart">
      <div
        className="subtitle"
        style={{ height: 33, display: 'flex', alignItems: 'center' }}
      >
        Actual vs Forecast
      </div>

      <Grid container>
        <canvas
          width={width}
          height={height - 20}
          ref={canvasRef}
          onMouseMove={onMouseMove}
          onMouseUp={endDraw}
          onMouseOut={onMouseOut}
          style={{ position: 'fixed', zIndex: canvasIndex }}
        />
        <svg
          width={width}
          height={height}
          onMouseDown={onSvgMouseDown}
          onMouseMove={onSvgMouseMove}
          onMouseUp={onSvgMouseUp}
          onMouseLeave={onSvgMouseLeave}
        >
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="transparent"
            onMouseMove={(event) => {
              event = event || window.event;
              if ('buttons' in event) {
                if (event.buttons === 1) {
                  setCanvasIndex(0);
                }
              }
            }}
          />

          <Group top={margin.top} left={margin.left}>
            <BarStack
              data={data}
              keys={selectedYears}
              x={xAccessor}
              xScale={xScale}
              yScale={yScale}
              color={color}
            >
              {(barStacks) => {
                return barStacks.map((barStack) => {
                  return barStack.bars.map((bar) => {
                    const height = Math.abs(bar.height);
                    const y = bar.height >= 0 ? bar.y : bar.y - height;
                    
                    return (
                      <rect
                        key={`bar-income-${barStack.index}-${bar.index}`}
                        x={bar.x}
                        y={y}
                        height={height}
                        width={bar.width}
                        fill={getColor(bar)}
                        onClick={(event) => handleBar(event, bar)}
                        onMouseLeave={(event) => hideTooltipHandler()}
                        onMouseMove={(event) => showTooltipHandler(event, xScale, bar)}
                        onTouchEnd={(event) => hideTooltipHandler()}
                        onTouchMove={(event) => showTooltipHandler(event, xScale, bar)}
                      />
                    );
                  });
                });
              }}
            </BarStack>

            <LinePath
              data={[
                { x: 0, y: yScale(0) },
                { x: width, y: yScale(0) },
              ]}
              x={(d) => d.x}
              y={(d) => d.y}
              stroke="#555555"
            />

            {forecasts.map((forecast, index) => {
              let value = data[index].value[selectedYears[0]] ? forecast : 0;
              const barWidth = (xMax - xScale.paddingInner() * xScale.step()) / fiscalMonths.length + offsetWidth;
              const x = (xScale.paddingInner() * xScale.step()) / 2 + index * xScale.step() + offsetX;
              const y = yScale(value) || 0;

              return (
                <Group key={`bar-forecast-${index}`}>
                  <Bar
                    width={barWidth}
                    height={1}
                    x={x}
                    y={y}
                    fill="black"
                    stroke="black"
                    strokeWidth={1}
                    onMouseLeave={(event) => hideTooltipHandler()}
                    onMouseMove={(event) => showTooltipHandler(event, xScale, value, false)}
                    onTouchEnd={(event) => hideTooltipHandler()}
                    onTouchMove={(event) => showTooltipHandler(event, xScale, value, false)}
                  />
                </Group>
              );
            })}

            <AxisLeft
              numTicks={4}
              scale={yScale}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'end',
                dy: '0.33em',
                dx: '-0.2em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps} fill="black">
                  ${formattedValue}
                </text>
              )}
            />

            {fiscalMonths.map((month, index) => {
              return (
                <rect
                  key={index}
                  x={
                    (xScale.paddingInner() * xScale.step()) / 2 +
                    index * xScale.step() +
                    offsetX
                  }
                  y={yMax ? yMax : 0}
                  width={
                    (xMax - (xScale.paddingInner() * xScale.step()) / 2) /
                      fiscalMonths.length +
                    offsetWidth
                  }
                  height={margin.bottom}
                  fill="transparent"
                  data-no-drag="true"
                  onClick={(event) => handleLabel(event, month)}
                />
              );
            })}

            <AxisBottom
              hideTicks={true}
              scale={xScale}
              top={yMax}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fontSize: 11,
                textAnchor: 'middle',
                dy: '-0.5em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text
                  {...tickProps}
                  style={{
                    fontWeight:
                      selectedMonths.indexOf(parseInt(formattedValue)) > -1 ? 700 : 500,
                  }}
                  fill={
                    selectedMonths.indexOf(parseInt(formattedValue)) > -1
                      ? activeLabelColor
                      : 'black'
                  }
                  onClick={(event) =>
                    handleLabel(event, parseInt(formattedValue))
                  }
                >
                  {enMonths[formattedValue]}
                </text>
              )}
            />
          </Group>
        </svg>

        {tooltipOpen && (
          <TooltipWithBounds
           
            top={tooltipTop + pageYOffset}
            left={tooltipLeft}
          >
            {tooltipData.isBar ? (
              <>
                {tooltipData.selSum !== 0 ? (
                  <div
                    className="ft-12 color-black"
                    style={{
                      backgroundColor: '#dddddd',
                      margin: 0,
                      minHeight: 25,
                    }}
                  >
                    <div style={{ padding: 5 }}>
                      {tooltipData.items} items selected SUM:&nbsp;
                      <strong>{thousandFormat2(tooltipData.selSum)}</strong>
                    </div>
                  </div>
                ) : null}
                <div className="color-black mt-5">
                  <div className="pdv-10">
                    <strong>
                      FY {tooltipData.key} :{' '}
                      {enMonths[xAccessor(tooltipData.bar.data)]}
                    </strong>
                  </div>
                  <div className="ft-12">
                    <strong>Actual</strong>: $
                    {thousandFormat(
                      tooltipData.bar.data.value[tooltipData.key]?.ActualFees,
                    )}
                  </div>
                  <div className="ft-12">
                    <strong>Forecast</strong>: $
                    {thousandFormat(
                      tooltipData.bar.data.value[tooltipData.key]
                        .ForecastFees,
                    )}
                  </div>
                  <br />
                  <div className="ft-12">
                    <strong>YTD Income</strong>: $
                    {thousandFormat(
                      ytdTotalActualArr[xAccessor(tooltipData.bar.data)],
                    )}
                  </div>
                  <div className="ft-12">
                    <strong>YTD Forecast</strong>: $
                    {thousandFormat(
                      ytdTotalForecastArr[xAccessor(tooltipData.bar.data)],
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="color-black mt-5">
                <strong>Forecast Fees</strong>: $
                {thousandFormat(tooltipData.forecast)}
              </div>
            )}
          </TooltipWithBounds>
        )}
      </Grid>
    </div>
  );
};

TopChart.propTypes = {
  summaryData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedMonths: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default TopChart;
